import { ArrowUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

type ScrollTarget =
  | { kind: 'container'; element: HTMLElement }
  | { kind: 'page'; element: HTMLElement };

const TABLE_SELECTOR = 'table, [role="table"].processing-table';
const PAGE_SCROLL_THRESHOLD = 420;
const CONTAINER_SCROLL_THRESHOLD = 280;
const TOPBAR_OFFSET = 84;

function internalScrollerFor(table: HTMLElement): HTMLElement | null {
  let element = table.parentElement;

  while (element && !element.classList.contains('app-main')) {
    const { overflowY } = window.getComputedStyle(element);
    const canScrollVertically = /(auto|scroll)/.test(overflowY)
      && element.scrollHeight > element.clientHeight + 2;

    if (canScrollVertically) return element;
    element = element.parentElement;
  }

  return null;
}

function visibleTarget(preferredScroller?: HTMLElement | null): ScrollTarget | null {
  const tables = Array.from(document.querySelectorAll<HTMLElement>(TABLE_SELECTOR));
  if (!tables.length) return null;

  const internalTargets = tables
    .map((table) => ({ table, scroller: internalScrollerFor(table) }))
    .filter((item): item is { table: HTMLElement; scroller: HTMLElement } => Boolean(item.scroller));

  if (preferredScroller?.scrollTop && preferredScroller.scrollTop > CONTAINER_SCROLL_THRESHOLD) {
    const preferred = internalTargets.find(({ scroller }) => scroller === preferredScroller);
    if (preferred) return { kind: 'container', element: preferred.scroller };
  }

  const scrolledContainer = internalTargets.find(({ scroller }) => {
    const rect = scroller.getBoundingClientRect();
    return scroller.scrollTop > CONTAINER_SCROLL_THRESHOLD
      && rect.bottom > TOPBAR_OFFSET
      && rect.top < window.innerHeight;
  });
  if (scrolledContainer) return { kind: 'container', element: scrolledContainer.scroller };

  const pageTables = tables.filter((table) => !internalTargets.some((item) => item.table === table));
  const activeTable = pageTables
    .filter((table) => {
      const rect = table.getBoundingClientRect();
      return rect.top < TOPBAR_OFFSET - PAGE_SCROLL_THRESHOLD && rect.bottom > TOPBAR_OFFSET;
    })
    .sort((first, second) => second.getBoundingClientRect().top - first.getBoundingClientRect().top)[0];

  return activeTable ? { kind: 'page', element: activeTable } : null;
}

export function TableScrollToTop() {
  const { pathname } = useLocation();
  const [target, setTarget] = useState<ScrollTarget | null>(null);

  useEffect(() => {
    let animationFrame = 0;

    const update = (preferredScroller?: HTMLElement | null) => {
      animationFrame = 0;
      const nextTarget = visibleTarget(preferredScroller);
      setTarget((current) => (
        current?.kind === nextTarget?.kind && current?.element === nextTarget?.element
          ? current
          : nextTarget
      ));
    };

    const scheduleUpdate = (event?: Event) => {
      const preferredScroller = event?.target instanceof HTMLElement ? event.target : null;
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => update(preferredScroller));
    };

    const resizeObserver = new ResizeObserver(() => scheduleUpdate());
    resizeObserver.observe(document.body);
    document.addEventListener('scroll', scheduleUpdate, { capture: true, passive: true });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    scheduleUpdate();

    return () => {
      resizeObserver.disconnect();
      document.removeEventListener('scroll', scheduleUpdate, { capture: true });
      window.removeEventListener('resize', scheduleUpdate);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, [pathname]);

  const scrollToTableStart = () => {
    if (!target) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    if (target.kind === 'container') {
      target.element.scrollTo({ top: 0, behavior });
      return;
    }

    const top = window.scrollY + target.element.getBoundingClientRect().top - TOPBAR_OFFSET;
    window.scrollTo({ top: Math.max(0, top), behavior });
  };

  if (!target) return null;

  return (
    <button
      className="table-scroll-top"
      type="button"
      aria-label="Torna all'inizio della tabella"
      title="Torna all'inizio della tabella"
      onClick={scrollToTableStart}
    >
      <ArrowUp size={18} strokeWidth={2.2} />
      <span>Torna su</span>
    </button>
  );
}
